import { Button, Heading, toast } from '@medusajs/ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type { LoanRequestInfo } from '@marxbiotech/shared-types/bff';

import { getApiErrorMessage } from '@lib/util/get-api-error-message';
import { ACCOUNT_LAST_DIGITS_LENGTH } from '@lib/constants/payment';
import {
  CreateSettlementRequestInput,
  useCreateSettlementRequest,
} from '@lib/query/mutations/payment';
import { convertToLocale } from '@lib/util/money';
import { FormField, FormLabel } from '@modules/common/components/form-field';
import Input from '@modules/common/components/input';
import Modal from '@modules/common/components/modal';

interface OrderPaymentNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  loanRequest: LoanRequestInfo;
  orderId: string;
  orderNumber: string;
}

interface FormData {
  accountLastFive: string;
  remittanceDate: string;
  remark: string;
}

export const OrderPaymentNotificationModal = ({
  isOpen,
  onClose,
  loanRequest,
  orderId,
  orderNumber,
}: OrderPaymentNotificationModalProps) => {
  const todayDate = useMemo(
    () => new Date().toISOString().split('T')[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      accountLastFive: '',
      remittanceDate: todayDate,
      remark: '',
    },
  });

  const formattedDebt = convertToLocale({
    amount: loanRequest.requestedAmount,
    currency_code: 'twd',
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const { mutate: createSettlementRequest, isPending } =
    useCreateSettlementRequest({
      onSuccess: () => {
        toast.success('繳款通知已送出');
        handleClose();
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, '繳款通知送出失敗'));
      },
    });

  const onSubmit = (data: FormData) => {
    const input: CreateSettlementRequestInput = {
      amount: loanRequest.requestedAmount,
      createdByType: 'CUSTOMER',
      bankInfo: {
        account: data.accountLastFive,
        date: data.remittanceDate,
      },
      reference: {
        type: 'ORDER',
        orderId,
        orderNumber,
      },
      loanRequestId: loanRequest.id,
      customerRemark: data.remark || undefined,
    };

    createSettlementRequest(input);
  };

  return (
    <Modal
      isOpen={isOpen}
      close={handleClose}
      data-testid="order-payment-notification-modal"
      panelClassName="max-h-[90vh] max-w-[600px] border-0 bg-white-10"
    >
      <Modal.Title>
        <Heading className="mb-4 text-xl font-bold">繳款通知</Heading>
      </Modal.Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="flex max-h-[60vh] w-full flex-col gap-y-4 overflow-y-auto">
            <FormField>
              <FormLabel className="mb-1">訂單單號</FormLabel>
              <Input
                name="order_number_display"
                value={orderNumber}
                readOnly
                data-testid="order-number-input"
              />
            </FormField>
            <FormField>
              <FormLabel className="mb-1">金額</FormLabel>
              <Input
                name="amount_display"
                value={formattedDebt}
                readOnly
                data-testid="order-debt-display"
              />
            </FormField>
            <FormField>
              <FormLabel className="mb-1">帳號末五碼*</FormLabel>
              <Input
                placeholder="請輸入帳號末五碼"
                {...register('accountLastFive', {
                  required: '請輸入帳號末五碼',
                  pattern: {
                    value: new RegExp(`^\\d{${ACCOUNT_LAST_DIGITS_LENGTH}}$`),
                    message: `請輸入${ACCOUNT_LAST_DIGITS_LENGTH}位數字`,
                  },
                })}
                maxLength={ACCOUNT_LAST_DIGITS_LENGTH}
                inputMode="numeric"
                error={errors.accountLastFive}
                data-testid="account-last-five-input"
              />
            </FormField>
            <FormField>
              <FormLabel className="mb-1">匯款日期*</FormLabel>
              <Input
                type="date"
                placeholder="請選擇匯款日期"
                {...register('remittanceDate', {
                  required: '請選擇匯款日期',
                })}
                error={errors.remittanceDate}
                data-testid="remittance-date-input"
              />
            </FormField>
            <FormField>
              <FormLabel className="mb-1">備註</FormLabel>
              <textarea
                placeholder="請輸入備註（選填）"
                {...register('remark')}
                rows={3}
                className="border-ui-border-base bg-ui-bg-field placeholder:text-ui-fg-muted focus:border-ui-border-interactive w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
                data-testid="remark-input"
              />
            </FormField>
          </div>
        </Modal.Body>
        <Modal.Footer className="mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="border-black-10 text-black-10 flex-none rounded-sm border border-solid px-4 py-2"
            data-testid="cancel-button"
          >
            取消
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            className="bg-primary-50 text-white-10 flex-none rounded-sm px-4 py-2 shadow-none"
            data-testid="submit-button"
          >
            {isPending ? '送出中...' : '送出'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default OrderPaymentNotificationModal;
